import { useRef } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { timelineEvents, objectives } from '../../mocks/beaData';
import useScrollReveal from '@/hooks/useScrollReveal';
import aboutHero from '@/assets/about-hero.jpg';
import aboutTeam from '@/assets/about-team.jpg';
import ajayImage from "@/assets/Ajay Kumar Singh.jpeg";
import kunalImage from "@/assets/Kunal Kishore.jpg";
import dilipImage from "@/assets/1759132288181.jpg";

const values = [
  { icon: 'ri-shield-check-line', title: 'Integrity', desc: 'Honesty and transparency in all government interactions and member representation.' },
  { icon: 'ri-team-line', title: 'Collaboration', desc: 'Bridging manufacturers, state bodies, and central institutions seamlessly.' },
  { icon: 'ri-seedling-line', title: 'Sustainability', desc: 'Championing ethanol as a green fuel to reduce carbon emissions across India.' },
  { icon: 'ri-line-chart-line', title: 'Growth', desc: 'Facilitating investment and market access for Bihar\'s distillery sector.' },
];


const keyMembers = [
  {
    name: "Dr. Dilip Patel",
    role: "Chairman, Bihar Ethanol Association",
    linkedin: "https://www.linkedin.com/in/drdilippatelcmd/",
    image: dilipImage,
    bio: [
      "Dr. Dilip Patel is the Chairman and Managing Director of Patel Group of Industries, one of Eastern India’s largest and most diversified agri-industrial conglomerates, and serves as Chairman of the Bihar Ethanol Association — bringing the weight of a proven industry builder to the state’s most consequential energy sector body.",
      "A medical doctor by training — holding MBBS from D.Y. Patil Medical College, Pune and a Fellowship in Onco-Anaesthesiology from AIIMS — Dr. Patel represents a rare combination of scientific temperament and entrepreneurial drive. He founded Patel Agri Industries Pvt. Ltd. in Nalanda, Bihar in 2013, establishing it as the anchor entity of what has grown into a diversified group with interests spanning agri-processing, ethanol production, healthcare, mining, and logistics, spread across 150 acres of operations.",
      "The Group operates three large rice mills with a combined milling capacity of 72 tonnes per hour, and runs a 300 KLD ethanol plant at Nalanda — one of the first and largest dedicated grain-based ethanol production facilities in Eastern India. The Group also produces and exports Corn DDGS, IR-64 rice, Katarni rice, and Sonam rice varieties, with export operations across six major ports. He exchanged an investment MoU with the Chief Minister of Bihar at the Bihar Business Connect 2023, reflecting the Group’s stature in the state’s industrial landscape.",
      "Beyond his own enterprise, Dr. Patel is a committed ecosystem builder. He serves as Official Mentor at Startup Bihar, guiding early-stage entrepreneurs, and has been an active voice in national policy advocacy as the Chairman of BEA’s industry delegations to the Union Ministry of Petroleum and Natural Gas and the Government of Bihar."
    ]
  },
  {
    name: "Mr. Ajay Kumar Singh",
    role: "President, Bihar Ethanol Association",
    linkedin: "https://www.linkedin.com/in/ajay-singh-5b988a255/",
    image: ajayImage,
    bio: [
      "Mr. Ajay Kumar Singh is the Chairman and Managing Director of Bharat Plus Group of Companies and Singh Group of Companies, and serves as President of the Bihar Ethanol Association — a role that reflects both his standing as one of the state’s most prominent industrial investors and his deep personal commitment to Bihar’s economic transformation.",
      "Mr. Singh spent the early part of his career working across India in diverse business roles before making a defining decision — to return to his homeland and invest in its future. That conviction led him to establish Bharat Plus Ethanol Pvt. Ltd. at Plot No. 10, BIADA Industrial Area, Nawanagar, Buxar — making him the first entrepreneur to set up a plant in what was then a greenfield industrial township of 486 acres surrounded entirely by paddy fields. His pioneering presence drew other investors, and Nawanagar has since been declared a Government of India economic corridor, with major beverage brands including Pepsi and Coca-Cola establishing plants in the vicinity.",
      "The Buxar ethanol plant, considered one of Bihar’s leading units, employs approximately 700 workers, of whom 300 are skilled — with 80% of the workforce drawn from villages within a 15-kilometre radius. Adjacent to the ethanol plant, Mr. Singh has also established a rice mill and a solvent plant, collectively employing an additional 250 local workers. His group’s integrated industrial presence in Buxar has become a symbol of reverse migration and rural employment generation in Bihar.",
      "Mr. Singh has been at the forefront of industry advocacy, personally briefing Chief Minister Nitish Kumar and Deputy Chief Minister Samrat Chaudhary during their inspection of his Buxar plant, and leading delegations to the Union Ministry of Petroleum and Natural Gas to represent the concerns of Bihar’s ethanol producers."
    ]
  },
  {
    name: "CA Kunal Kishore",
    role: "Secretary General, Bihar Ethanol Association",
    linkedin: "https://www.linkedin.com/in/kunalkishore/",
    image: kunalImage,
    bio: [
      "CA Kunal Kishore is an All India Rank 6 Chartered Accountant with over 15 years of experience spanning industrial finance, government policy, and entrepreneurship. Originally from Muzaffarpur and based in Patna, he serves as Secretary General of the Bihar Ethanol Association, representing the state’s 22 grain-based distillery units before the Central Government, Oil Marketing Companies, and national industry bodies.",
      "CA Kunal began his career with HCL Corporation and Trifecta Capital, India’s pioneering venture debt fund, before transitioning to government advisory work as Lead Consultant with the State Investment Promotion Board (SIPB), Department of Industries, Government of Bihar. In this capacity, he drafted some of Bihar’s most consequential industrial policies, including the Bihar Ethanol Production Promotion Policy 2021 — the landmark framework that catalysed the state’s current wave of ethanol investment — along with the Chief Minister Udyami Yojana, Oxygen Production Promotion Policy 2021, and Bihar Startup Policy.",
      "In 2018, he founded InvestAid India Pvt. Ltd., an industrial project consultancy with offices in Patna, New Delhi, and Jaipur, specialising in financing, licensing, and subsidy advisory. The firm has facilitated debt syndication of USD 40 million, served over 295 industries across India, and counts 65+ ethanol plants among its clients nationally.",
      "He is Co-founder of Karekeba Ventures, Eastern India’s most active angel investment network, with a portfolio spanning health-tech, AI, agri-tech, and D2C startups. He also leads Kunal Kishore & Co., a Chartered Accountancy firm serving major corporations across Bihar, Jharkhand, Odisha, and West Bengal.",
      "A frequent speaker at industry forums and policy conclaves, CA Kunal has led high-level delegations to the Ministry of Petroleum and Natural Gas, advocating for equitable ethanol allocation for Bihar’s producers."
    ]
  }
];
const About = () => {
  const pageRef = useRef<HTMLDivElement>(null);
  useScrollReveal(pageRef);

  return (
    <div className="font-jakarta bg-white">
      <Navbar />
      <main ref={pageRef}>

        {/* Page Header */}
        <section className="pt-14 bg-white">
          <div className="max-w-4xl mx-auto px-6 md:px-10 pt-14 pb-12 text-center">
            <div data-reveal="fade" data-delay="1" className="mb-4">
              <span className="text-[11px] font-bold text-bteal tracking-widest uppercase">About BEA</span>
            </div>
            <h1 data-reveal data-delay="2" className="text-4xl md:text-5xl font-bold text-ink leading-tight tracking-tight mb-4">
              About Bihar Ethanol Association
            </h1>
            <p data-reveal data-delay="3" className="text-ink-muted text-lg max-w-xl mx-auto leading-relaxed">
              Established to empower Bihar&apos;s ethanol manufacturers and shape the state&apos;s biofuel policy — for a cleaner, greener India.
            </p>
          </div>
          <div className="max-w-7xl mx-auto px-6 md:px-10 pb-0">
            <div data-reveal="scale" data-delay="4" className="rounded-lg overflow-hidden w-full" style={{ height: '360px' }}>
              <img
                src={aboutHero}
                alt="BEA Leadership Meeting"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
          <div className="h-px bg-gray-100 mt-0" />
        </section>

        {/* Key Members */}
        <section id="key-members" className="py-16 md:py-24 bg-surface">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="text-center mb-16">
              <span data-reveal="fade" data-delay="1" className="text-[11px] font-bold text-bteal tracking-widest uppercase block">Leadership</span>
              <h2 data-reveal data-delay="2" className="text-3xl md:text-4xl font-bold text-ink mt-2 tracking-tight">Key Members of BEA</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {keyMembers.map((member, i) => (
                <div
                  key={i}
                  data-reveal="fade"
                  data-delay={String((i % 3) + 1)}
                  className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center text-center h-[560px] overflow-hidden transition-all duration-300"
                >
                  <div className="flex-shrink-0 flex flex-col items-center">
                    {member.image ? (
                      <div className="w-32 h-32 rounded-full overflow-hidden mb-5 border-4 border-white shadow-md">
                        <img src={member.image} alt={member.name} className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <div className="w-32 h-32 rounded-full bg-bteal/10 flex items-center justify-center mb-5 border-4 border-white shadow-sm">
                        <span className="text-4xl font-bold text-bteal">{member.name.replace(/^(Mr\.|Dr\.|CA)\s*/i, '').charAt(0)}</span>
                      </div>
                    )}
                    <h3 className="text-xl font-bold text-ink mb-1">{member.name}</h3>
                    <p className="text-bteal font-semibold text-xs mb-3">{member.role}</p>
                    {member.linkedin && (
                      <a href={member.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-8 h-8 text-[#0A66C2] hover:text-[#004182] transition-colors bg-[#0A66C2]/10 hover:bg-[#0A66C2]/20 rounded-md mb-4">
                        <i className="ri-linkedin-fill text-lg leading-none" />
                      </a>
                    )}
                  </div>
                  <div className="space-y-3 mt-2 text-left flex-1 overflow-hidden hover:overflow-y-auto pr-2 pb-2">
                    {member.bio.map((paragraph, j) => (
                      <p key={j} className="text-ink-muted text-sm leading-relaxed">{paragraph}</p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section id="mission" className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
              <div>
                <span data-reveal="left" data-delay="1" className="text-[11px] font-bold text-bgreen tracking-widest uppercase block">Mission &amp; Vision</span>
                <h2 data-reveal="left" data-delay="2" className="text-3xl font-bold text-ink mt-2 mb-8 leading-tight tracking-tight">
                  A purpose-driven association for Bihar&apos;s energy future
                </h2>
                <div data-reveal="left" data-delay="3" className="space-y-3 mb-10">
                  <div className="p-5 bg-surface rounded-md border-l-4 border-bteal">
                    <h3 className="font-semibold text-ink mb-2 text-sm flex items-center gap-2">
                      <i className="ri-focus-3-line text-bteal text-sm" /> Our Mission
                    </h3>
                    <p className="text-ink-muted text-sm leading-relaxed">
                      To serve as the authoritative platform for Bihar&apos;s ethanol manufacturers — facilitating policy advocacy, procurement support, and industry representation before government and regulatory bodies.
                    </p>
                  </div>
                  <div className="p-5 bg-surface rounded-md border-l-4 border-bgreen">
                    <h3 className="font-semibold text-ink mb-2 text-sm flex items-center gap-2">
                      <i className="ri-eye-line text-bgreen text-sm" /> Our Vision
                    </h3>
                    <p className="text-ink-muted text-sm leading-relaxed">
                      Bihar becoming the leading state contributor to India&apos;s E20 ethanol blending mission, with world-class distilleries and a thriving rural economy rooted in biofuel.
                    </p>
                  </div>
                </div>
              </div>
              <div data-reveal="right" data-delay="2" className="relative rounded-lg overflow-hidden" style={{ height: '420px' }}>
                <img
                  src={aboutTeam}
                  alt="BEA Leadership Team"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-bteal" />
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-16 md:py-20 bg-bteal">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <span data-reveal="fade" data-delay="1" className="text-[11px] font-bold text-white/50 tracking-widest uppercase block">What Drives Us</span>
            <h2 data-reveal data-delay="2" className="text-3xl font-bold text-white mt-2 mb-10 tracking-tight">Our Core Values</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/15">
              {values.map((v, i) => (
                <div
                  key={i}
                  data-reveal
                  data-delay={String(i + 1)}
                  className="bg-bteal p-6 hover:bg-bteal-dark transition-colors duration-200"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-md bg-white/10 mb-5">
                    <i className={`${v.icon} text-white text-sm`} />
                  </div>
                  <h3 className="font-semibold text-white mb-2 text-sm">{v.title}</h3>
                  <p className="text-white/55 text-xs leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="py-16 md:py-28 bg-surface overflow-hidden">
          <div className="max-w-5xl mx-auto px-6 md:px-10">
            <div className="text-center mb-20">
              <span data-reveal="fade" data-delay="1" className="text-[11px] font-bold text-bgreen tracking-widest uppercase block">Our Journey</span>
              <h2 data-reveal data-delay="2" className="text-3xl md:text-4xl font-bold text-ink mt-2 tracking-tight">
                Milestones &amp; History
              </h2>
            </div>

            {/* Desktop Timeline */}
            <div className="hidden md:block relative">
              <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px bg-gray-200" />
              {timelineEvents.map((event, i) => {
                const isLeft = i % 2 === 0;
                const accents = [
                  { nodeBg: '#28a99e', borderColor: '#28a99e', tagBg: '#edfaf9', tagText: '#1d8079' },
                  { nodeBg: '#008000', borderColor: '#008000', tagBg: '#f0faf0', tagText: '#006400' },
                  { nodeBg: '#F0C808', borderColor: '#c9a700', tagBg: '#fffce6', tagText: '#856a00' },
                ];
                const a = accents[i % 3];
                return (
                  <div
                    key={i}
                    data-reveal={isLeft ? 'left' : 'right'}
                    data-delay={String((i % 3) + 1)}
                    className="relative flex items-start mb-14"
                  >
                    <div className="flex-1 flex justify-end pr-12">
                      {isLeft ? (
                        <div className="relative w-full max-w-[340px] rounded-2xl p-6 bg-white hover:-translate-y-1 transition-transform duration-200" style={{ border: `1.5px solid ${a.borderColor}26`, borderLeft: `4px solid ${a.nodeBg}` }}>
                          <span className="absolute top-7 -right-12 w-12 h-px" style={{ background: `${a.nodeBg}55` }} />
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full mb-3" style={{ background: a.tagBg, color: a.tagText }}>
                            <i className="ri-time-line" style={{ fontSize: '9px' }} /> {event.year}
                          </span>
                          <h3 className="font-bold text-ink text-sm mb-2 leading-snug">{event.title}</h3>
                          <p className="text-ink-muted text-xs leading-relaxed">{event.description}</p>
                        </div>
                      ) : <div className="w-full max-w-[340px]" />}
                    </div>
                    <div className="absolute left-1/2 -translate-x-1/2 top-5 z-10 flex items-center justify-center">
                      <div className="w-5 h-5 rounded-full border-2 border-white" style={{ background: a.nodeBg, boxShadow: `0 0 0 3px ${a.nodeBg}33` }} />
                    </div>
                    <div className="flex-1 flex justify-start pl-12">
                      {!isLeft ? (
                        <div className="relative w-full max-w-[340px] rounded-2xl p-6 bg-white hover:-translate-y-1 transition-transform duration-200" style={{ border: `1.5px solid ${a.borderColor}26`, borderLeft: `4px solid ${a.nodeBg}` }}>
                          <span className="absolute top-7 -left-12 w-12 h-px" style={{ background: `${a.nodeBg}55` }} />
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full mb-3" style={{ background: a.tagBg, color: a.tagText }}>
                            <i className="ri-time-line" style={{ fontSize: '9px' }} /> {event.year}
                          </span>
                          <h3 className="font-bold text-ink text-sm mb-2 leading-snug">{event.title}</h3>
                          <p className="text-ink-muted text-xs leading-relaxed">{event.description}</p>
                        </div>
                      ) : <div className="w-full max-w-[340px]" />}
                    </div>
                  </div>
                );
              })}
              <div className="flex justify-center"><div className="w-3 h-3 rounded-full bg-gray-300" /></div>
            </div>

            {/* Mobile Timeline */}
            <div className="md:hidden relative pl-8">
              <div className="absolute left-3 top-0 bottom-0 w-px bg-gray-200" />
              {timelineEvents.map((event, i) => {
                const accents = [
                  { nodeBg: '#28a99e', tagBg: '#edfaf9', tagText: '#1d8079' },
                  { nodeBg: '#008000', tagBg: '#f0faf0', tagText: '#006400' },
                  { nodeBg: '#F0C808', tagBg: '#fffce6', tagText: '#856a00' },
                ];
                const a = accents[i % 3];
                return (
                  <div key={i} data-reveal data-delay={String((i % 3) + 1)} className="relative mb-8">
                    <div className="absolute -left-8 top-5 w-4 h-4 rounded-full border-2 border-white translate-x-1" style={{ background: a.nodeBg, boxShadow: `0 0 0 2px ${a.nodeBg}44` }} />
                    <div className="rounded-2xl p-5 bg-white" style={{ border: `1.5px solid ${a.nodeBg}22`, borderLeft: `4px solid ${a.nodeBg}` }}>
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full mb-3" style={{ background: a.tagBg, color: a.tagText }}>
                        <i className="ri-time-line" style={{ fontSize: '9px' }} /> {event.year}
                      </span>
                      <h3 className="font-bold text-ink text-sm mb-1.5 leading-snug">{event.title}</h3>
                      <p className="text-ink-muted text-xs leading-relaxed">{event.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>



        {/* Objectives */}
        <section id="objectives" className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="text-center mb-12">
              <span data-reveal="fade" data-delay="1" className="text-[11px] font-bold text-bteal tracking-widest uppercase block">What We Do</span>
              <h2 data-reveal data-delay="2" className="text-3xl md:text-4xl font-bold text-ink mt-2 tracking-tight">Objectives &amp; Goals</h2>
              <p data-reveal data-delay="3" className="text-ink-muted text-sm mt-3 max-w-lg mx-auto leading-relaxed">
                BEA works across six key areas to strengthen Bihar&apos;s ethanol sector and support its members.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {objectives.map((obj, i) => (
                <div
                  key={i}
                  data-reveal="scale"
                  data-delay={String((i % 3) + 1)}
                  className="p-6 rounded-lg border border-gray-100 bg-surface hover:border-bteal/20 hover:-translate-y-0.5 transition-all duration-200"
                >
                  <div className="w-9 h-9 flex items-center justify-center rounded-md bg-bteal/8 mb-4">
                    <i className={`${obj.icon} text-bteal text-base`} />
                  </div>
                  <h3 className="font-semibold text-ink text-sm mb-2">{obj.title}</h3>
                  <p className="text-ink-muted text-xs leading-relaxed">{obj.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>


      </main>
      <Footer />
    </div>
  );
};

export default About;
